import { addDays } from './timezone'
import { submissionHref, walkHref } from './listLinks'
import type { HomeActivity, HomeFiveS, Task } from '@/types/api'

/**
 * The arithmetic behind Početna, kept out of the components so it can be
 * tested on its own: how far through the month 5S is, which tasks count as
 * "soon", and where each line of the activity list leads.
 */

/** How many days ahead a task's deadline counts as "soon" — a working week. */
export const DUE_SOON_DAYS = 7

/**
 * How much of this month's 5S is done.
 *
 * `percent` is rounded down, so the bar never reads 100% while a machine is
 * still waiting — 7 of 8 is 87%, not a rounded-up 88% that looks finished at a
 * glance. Null for a plant with no active machines, which has nothing to cover.
 */
export function coverage(fiveS: Pick<HomeFiveS, 'walked' | 'machines'>): {
  walked: number
  machines: number
  percent: number | null
  done: boolean
} {
  const { walked, machines } = fiveS

  return {
    walked,
    machines,
    percent: machines > 0 ? Math.floor((walked / machines) * 100) : null,
    done: machines > 0 && walked >= machines,
  }
}

/**
 * The machines still to walk, grouped by plant in the order the server sent.
 *
 * Grouped only to be labelled: somebody with one plant gets one group and the
 * screen prints no heading for it.
 */
export function missingByPlant(missing: HomeFiveS['missing']): {
  plant: { id: string; name: string } | null
  machines: HomeFiveS['missing']
}[] {
  const groups: { plant: { id: string; name: string } | null; machines: HomeFiveS['missing'] }[] = []

  for (const machine of missing) {
    const key = machine.plant?.id ?? null
    const group = groups.find((g) => (g.plant?.id ?? null) === key)

    if (group) group.machines.push(machine)
    else groups.push({ plant: machine.plant, machines: [machine] })
  }

  return groups
}

/**
 * Which of somebody's open tasks need them this week.
 *
 * Late first, then by deadline; a task with no deadline is never "soon" — it
 * is still on the list behind the number, just not on the dashboard.
 * `today` is the company's date, `YYYY-MM-DD`, passed in so the rule can be
 * tested on a fixed day.
 */
export function tasksNeedingMe<T extends Pick<Task, 'dueDate' | 'isOverdue'>>(
  tasks: T[],
  today: string,
  days = DUE_SOON_DAYS,
): { overdue: number; soon: T[] } {
  const horizon = addDays(today, days)

  const soon = tasks
    .filter((task) => task.isOverdue || (task.dueDate !== null && task.dueDate.slice(0, 10) <= horizon))
    .sort((a, b) => {
      if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1

      return (a.dueDate ?? '').localeCompare(b.dueDate ?? '')
    })

  return { overdue: tasks.filter((task) => task.isOverdue).length, soon }
}

/** Where one line of "Nedavno u vašim pogonima" leads. */
export function activityHref(item: HomeActivity): string {
  return item.kind === 'walked' ? walkHref(item.evaluation.id) : submissionHref(item.submission.id)
}

/** The first day of the month `today` falls in. */
export function monthStart(today: string): string {
  return `${today.slice(0, 7)}-01`
}

/**
 * How many whole days a deadline is behind `today`, for "kasni 3 dana".
 *
 * Calendar days on the company's clock rather than 24-hour blocks: a report
 * due yesterday afternoon is a day late this morning, which is how a
 * supervisor counts it.
 */
export function daysLate(dueDate: string, today: string): number {
  const due = Date.UTC(...ymd(dueDate))
  const now = Date.UTC(...ymd(today))

  return Math.max(0, Math.round((now - due) / 86_400_000))
}

function ymd(date: string): [number, number, number] {
  const [year, month, day] = date.slice(0, 10).split('-').map(Number)

  return [year, month - 1, day]
}
